import React, { useEffect, useRef, useState } from "react";
import { FormLocationSelector } from "components/form-fields";
import { HubTile } from "components/hubs/hub-tile";
import { GridLayout } from "components/grid";
import { Tooltip } from "components/tooltip";
import { Icon, Intent, Position } from "@blueprintjs/core";
import { NotificationToaster } from "components/notifications";
import { LicenceCheckingService } from "services";
import { ShowHide } from "components/layout";
import { useSelector, useDispatch } from "react-redux";
import { EChartsRenderer } from 'components/charts';
import axios from "axios";
import "./LicenceCheckingOverview.css";
import classNames from "classnames";

export function LicenceCheckingOverview() {
    //General
    const [isLoading, setIsLoading] = useState(true);
    // Location selector
    const reduxLocations = useSelector(
        (state) => state.licenceChecking.locations
    );
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    // Data
    const [licenceCheckingOptions, setLicenceCheckingOptions] = useState({});
    const [onboardingStats, setOnboardingStats] = useState([]);
    const [checkStats, setCheckStats] = useState([]);
    //UI
    const [onBoardingTiles, setOnBoardingTiles] = useState([
        <HubTile key={1} isLoading />,
        <HubTile key={2} isLoading />,
        <HubTile key={3} isLoading />,
        <HubTile key={4} isLoading />,
        <HubTile key={5} isLoading />,
        <HubTile key={6} isLoading />,
    ]);
    const [checkStatsTiles, setCheckStatsTiles] = useState([
        <HubTile key={1} isLoading />,
        <HubTile key={2} isLoading />,
        <HubTile key={3} isLoading />,
        <HubTile key={4} isLoading />,
        <HubTile key={5} isLoading />,
        <HubTile key={6} isLoading />,
        <HubTile key={7} isLoading />,
        <HubTile key={8} isLoading />,
        <HubTile key={9} isLoading />,
        <HubTile key={10} isLoading />,
    ]);
    //Graph
    const [graphTitle, setGraphTitle] = useState("");
    const [graphPercentage, setGraphPercentage] = useState(0);
    const [graphCount, setGraphCount] = useState(0);
    const [graphTotal, setGraphTotal] = useState(0);
    const [graphColour, setGraphColour] = useState("#F0F0F0");

    const dispatch = useDispatch();

    // The licence-checking listing tabs widen the page via the site full-width flag.
    // Returning to this tab resets the flag, shrinking the container — the echarts
    // canvas does not re-fit to that change on its own quickly enough, so resize it
    // explicitly whenever the flag changes to avoid a transient oversized chart.
    const siteFullWidth = useSelector((state) => state.site.fullWidth);
    const chartRef = useRef(null);

    useEffect(() => {
        chartRef.current?.getEchartsInstance()?.resize();
    }, [siteFullWidth]);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }

        getStats();
    }, [reduxLocations]);

    useEffect(() => {
        if (!licenceCheckingOptions || onboardingStats.length === 0) {
            return;
        }

        setOnBoardingTiles(
            licenceCheckingOptions.onboardingStatuses.map((option) => {
                return (
                    <HubTile
                        key={option.id}
                        isLoading={isLoading}
                        title={option.name}
                        count={
                            onboardingStats.find((x) => x.id === option.id)
                                ?.count
                        }
                        colour={option.colour}
                        link={`/licence-checking/employee-onboarding?filter=${option.key}`}
                        description={option.description}
                    />
                );
            })
        );
    }, [licenceCheckingOptions, onboardingStats, isLoading]);

    useEffect(() => {
        if (!licenceCheckingOptions || checkStats.length === 0) {
            return;
        }

        setCheckStatsTiles(
            licenceCheckingOptions.checkFilters.map((option) => {
                let checkStat = checkStats.find((x) => x.id === option.id);

                return (
                    <HubTile
                        key={option.id}
                        isLoading={isLoading}
                        title={option.name}
                        count={checkStat?.count}
                        totalCount={checkStat?.totalPossibleCount}
                        colour={option.colour}
                        link={`/licence-checking/enrolled-employees?filter=${option.key}`}
                        description={option.description}
                        setGraphHover={setGraphHover}
                    />
                );
            })
        );
    }, [licenceCheckingOptions, checkStats, isLoading]);

    function getStats() {
        setIsLoading(true);
        setGraphTitle("");
        setGraphPercentage(0);
        setGraphCount(0);
        setGraphTotal(0);
        setGraphColour("#F0F0F0");
        axios
            .all([
                LicenceCheckingService.getLicenceCheckingOptions(),
                LicenceCheckingService.getOnboardingStats({
                    locationIds: reduxLocations,
                }),
                LicenceCheckingService.getCheckStats({
                    locationIds: reduxLocations,
                }),
            ])
            .then(
                axios.spread(function (
                    options,
                    onboardingStatsData,
                    checkStatsData
                ) {
                    setLicenceCheckingOptions(options);
                    setOnboardingStats(onboardingStatsData);
                    setCheckStats(checkStatsData);
                }),
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Unable to load data. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Unable to load data. Please refresh the page."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_LICENCE_CHECKING_LOCATIONS",
            payload: locations,
        });
    }

    function setGraphHover(graph) {
        if (!graph) {
            return
        }

        setGraphTitle(graph?.title);
        setGraphCount(graph?.count);
        setGraphTotal(graph?.totalCount);
        setGraphPercentage(
            Math.round((graph?.count / graph?.totalCount) * 100)
        );
        setGraphColour(graph?.colour);
    }

    return (
        <>
            <div className="inline-items spacer-bottom">
                <FormLocationSelector
                    businessArea={"Tacho"}
                    loading={false}
                    selectedLocations={reduxLocations}
                    setSelectedLocations={onSelectedLocationsChange}
                    useLocationDefaults={useLocationDefaults}
                    useHierarchy={useHierarchy}
                    setUseHierarchy={setUseHierarchy}
                    locationDefaultsToTrue={isLoading ? null : true}
                />
                <Tooltip
                    position={Position.RIGHT}
                    content={
                        <div>
                            <h4>Location Filter</h4>
                            <p>
                                This location filter is applied to both Employee
                                Onboarding and Enrolled Employees and will be
                                carried through to the listing views for both
                                datasets.
                            </p>
                        </div>
                    }
                >
                    <Icon
                        className="hub-tool-tip"
                        icon="info-sign"
                        size={15}
                        color="#0C2235"
                    />
                </Tooltip>
            </div>

            <h2>Employee Onboarding</h2>

            <div className="licence-checking-onboarding-row spacer-bottom">
                {onBoardingTiles}
            </div>

            <h2>Enrolled Employees</h2>
            <GridLayout numberOfColumns={2} gap={10}>
                <div className={classNames("licence-checking-outside-pie-chart", { 'bp6-skeleton': isLoading })}>
                    <div className="licence-checking-chart-key-title">
                        Chart Key
                    </div>
                    <ShowHide
                        evaluator={graphTitle !== ""}
                        show={
                            <div className="licence-checking-chart-key-sub-title">
                                {graphTitle}
                            </div>
                        }
                        hide={
                            <div className="licence-checking-chart-key-helper">
                                Please hover over check categories to view data
                            </div>
                        }
                    />

                    <div className="licence-checking-chart-holder">
                        <ShowHide
                            evaluator={isLoading}
                            hide={
                                <div className="licence-checking-chart-stat">
                                    <div className="licence-checking-chart-stat-percentage">
                                        <p>{graphPercentage}%</p>
                                    </div>
                                    <div className="licence-checking-chart-stat-count">
                                        <p>
                                            {graphCount}/{graphTotal}
                                        </p>
                                    </div>
                                </div>
                            }
                        />
                        <div className="licence-checking-chart">
                            <EChartsRenderer
                                ref={chartRef}
                                className="licence-checking-pie-chart"
                                option={{
                                    series: [
                                        {
                                            type: "pie",
                                            radius: ["40%", "90%"],
                                            avoidLabelOverlap: true,
                                            itemStyle: {},
                                            label: {
                                                show: false,
                                            },
                                            emphasis: {
                                                label: {
                                                    show: false,
                                                },
                                            },
                                            labelLine: {
                                                show: false,
                                            },
                                            data: [
                                                {
                                                    value: graphCount,
                                                    itemStyle: {
                                                        color: graphColour,
                                                    },
                                                },
                                                {
                                                    value:
                                                        graphTotal - graphCount,
                                                    itemStyle: {
                                                        color: "#F0F0F0",
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                }}
                            />
                        </div>
                    </div>
                </div>

                <GridLayout numberOfColumns={3} gap={10}>
                    {checkStatsTiles}
                </GridLayout>
            </GridLayout>
        </>
    );
}
